import config from '@conf'
import { tokensService } from '@api/tokens.service'
import { reviewableRequestsService } from '@api/reviewable_requests.service'
import { salesService } from '@api/sales.service'
import { RecordFactory } from '@js/records/factory'
import { Paginator } from '@js/helpers/paginator'
import { saleGetList } from '@const'
import { vuexTypes } from '../types'
import cloneDeep from 'lodash/cloneDeep'

function getInitialState () {
  return {
    tokenCreationRequests: new Paginator({
      txPerPage: config.REQUESTS_PER_PAGE,
      recordWrp: RecordFactory.createTokenCreationRecord.bind(RecordFactory)
    }),
    preIssuanceUploadRequests: new Paginator({
      txPerPage: config.REQUESTS_PER_PAGE,
      recordWrp: RecordFactory.createPreIssuanceRequestRecord.bind(RecordFactory)
    }),
    contractCreationRequests: new Paginator({
      txPerPage: config.SIGNATURE_REQUESTS_PER_PAGE,
      recordWrp: RecordFactory.createContractCreationRequestRecord.bind(RecordFactory)
    }),
    invoicesRequests: new Paginator({
      txPerPage: config.SIGNATURE_REQUESTS_PER_PAGE,
      recordWrp: RecordFactory.createInvoiceRequestRecord.bind(RecordFactory)
    }),
    saleCreationRequests: {},
    limitsRequests: new Paginator({
      recordWrp: RecordFactory.createLimitRequestRecord.bind(RecordFactory)
    }),
    isInitialized: false
  }
}

const state = getInitialState()

const mutations = {
  SET_LOGGED_OUT_STATE (state) {
    Object.assign(state, getInitialState())
  },
  UPDATE_SALE_REQUEST_LIST (state, tokenCodes) {
    tokenCodes.forEach(code => {
      state.saleCreationRequests[code] = new Paginator({
        recordWrp: (record) => RecordFactory.createSaleCreationRecord(record, code)
      })
    })
  },

  UPDATE_SALE_REQUEST_LIST_ITEM (state, { code, paginator }) {
    const lists = cloneDeep(state.saleCreationRequests)
    lists[code] = paginator
    state.saleCreationRequests = lists
  },

  SET_SALE_REQUEST_LIST_INITIALIZED (state) {
    state.isInitialized = true
  }
}

const actions = {
  INIT_SALE_REQUESTS_LIST ({ commit, rootGetters }) {
    const tokenCodes = Object.keys(rootGetters.accountOwnedTokens)
    commit(vuexTypes.UPDATE_SALE_REQUEST_LIST, tokenCodes)
    commit(vuexTypes.SET_SALE_REQUEST_LIST_INITIALIZED)
  },

  async GET_USER_TOKENS_CREATION_REQUESTS ({ state }) {
    state.tokenCreationRequests.attachInitLoader(() => tokensService.loadTokenCreationRequestsForState())
    return state.tokenCreationRequests.init()
  },

  async NEXT_USER_TOKENS_CREATION_REQUESTS ({ state }) {
    return state.tokenCreationRequests.next()
  },

  async GET_USER_PREISSUANCE_UPLOAD_REQUESTS ({ state }) {
    state.preIssuanceUploadRequests.attachInitLoader(() => reviewableRequestsService.loadPreIssuanceRequests())
    return state.preIssuanceUploadRequests.init()
  },

  async NEXT_USER_PREISSUANCE_UPLOAD_REQUESTS ({ state }) {
    return state.preIssuanceUploadRequests.next()
  },

  async GET_USER_SALE_CREATION_REQUESTS ({ state, dispatch, commit }, tokenCode) {
    if (!state.isInitialized) dispatch(vuexTypes.INIT_SALE_REQUESTS_LIST)
    let paginator
    if (state.saleCreationRequests[tokenCode]) {
      paginator = state.saleCreationRequests[tokenCode]
    } else {
      state.saleCreationRequests[tokenCode] = new Paginator({
        recordWrp: record => RecordFactory.createSaleCreationRecord(record, tokenCode)
      })
      paginator = state.saleCreationRequests[tokenCode]
    }

    let loadRequest
    if (tokenCode === saleGetList.saleList) {
      loadRequest = null
    } else {
      loadRequest = tokenCode
    }
    paginator.attachInitLoader(() => salesService.loadSalesRequests(loadRequest))

    await paginator.init()
    commit(vuexTypes.UPDATE_SALE_REQUEST_LIST_ITEM, { tokenCode, paginator })
  },

  async NEXT_USER_SALE_CREATION_REQUESTS ({ state, commit, dispatch }, tokenCode) {
    const paginator = state.saleCreationRequests[tokenCode]
    await paginator.next()
    commit(vuexTypes.UPDATE_SALE_REQUEST_LIST_ITEM, paginator)
  },

  async GET_LIMITS_REQUESTS ({ state }) {
    const limitsRequests = state.limitsRequests
    limitsRequests.attachInitLoader(reviewableRequestsService.loadLimitsUpdateReviewableRequests.bind(reviewableRequestsService))
    await limitsRequests.init()
  },

  async NEXT_LIMITS_REQUESTS ({ state }) {
    await state.limitsRequests.next()
  },

  async GET_USER_CONTRACT_CREATION_REQUESTS ({ state }, filters) {
    state.contractCreationRequests.attachInitLoader(() => reviewableRequestsService.loadContractsReviewableRequests(filters))
    return state.contractCreationRequests.init()
  },

  async NEXT_USER_CONTRACT_CREATION_REQUESTS ({ state }) {
    return state.contractCreationRequests.next()
  },

  async GET_USER_INVOICES_REQUESTS ({ state }, contractId) {
    state.invoicesRequests.attachInitLoader(() => reviewableRequestsService.loadInvoicesReviewableRequests(contractId))
    return state.invoicesRequests.init()
  },

  async NEXT_USER_INVOICES_REQUESTS ({ state }) {
    return state.invoicesRequests.next()
  }
}

const getters = {
  tokenCreationRequests: state => state.tokenCreationRequests,
  preIssuanceUploadRequests: state => state.preIssuanceUploadRequests,
  saleCreationRequests: state => state.saleCreationRequests,
  limitsRequests: state => state.limitsRequests.records,
  isLimitsRequestsLoaded: state => state.limitsRequests.isLoaded,
  contractCreationRequests: state => state.contractCreationRequests,
  invoicesRequests: state => state.invoicesRequests
}

export default {
  actions,
  getters,
  mutations,
  state
}



// WEBPACK FOOTER //
// ./src/vuex/modules/requests.module.js